import { useCallback, useEffect, useState } from 'react';
import {
  auditExportUrl,
  fetchAudit,
  fetchHolds,
  fetchRetention,
  liftHold,
  placeHold,
} from './api';
import type { AuditEvent, Hold, Retention } from './api';

/**
 * The page for the person who has to answer for it.
 *
 * Three questions, in the order somebody is asked them: how long is this kept,
 * what must not be deleted yet, and who has looked. They are one page because
 * they are one argument — a retention policy without an audit log cannot show
 * that it was followed, and a hold nobody can see looks like a malfunction when
 * a recording fails to age out.
 *
 * MOST OF IT IS VISIBLE TO ALL STAFF, and only the acting is restricted.
 * Retention and the list of holds explain why the archive behaves as it does,
 * and routing that explanation through an operator is how it goes unanswered.
 * Placing a hold, lifting one and reading the access log are operators only.
 */

function when(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** "30d" and "forever" both read better than a duration in hours. */
function period(v: string): string {
  switch (v) {
    case 'forever':
      return 'kept indefinitely';
    default:
      return v;
  }
}

function RetentionPanel() {
  const [r, setR] = useState<Retention | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchRetention()
      .then(setR)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  if (error) return <p className="mx-error">{error}</p>;
  if (!r) return <p className="mx-muted">Loading…</p>;

  const rows: { label: string; p: Retention['default'] }[] = [
    { label: 'Everything else', p: r.default },
    ...Object.entries(r.rooms).map(([room, p]) => ({ label: room, p })),
  ];

  return (
    <section className="mx-dash-section">
      <div className="mx-room-head">
        <h2>Retention</h2>
        {!r.active && <span className="mx-pill mx-pill-quiet">nothing is deleted</span>}
      </div>
      <p className="mx-muted">
        Video is the expensive part and rarely the useful part, so the usual policy prunes it
        early and keeps audio, the transcript and the manifest. A track whose media has been
        pruned stays in the manifest as a row — the bytes go, the fact that it existed does not.
      </p>
      <div className="mx-tablewrap">
        <table className="mx-table">
          <thead>
            <tr>
              <th>Room</th>
              <th>Video</th>
              <th>Audio</th>
              <th>Transcript</th>
              <th>The meeting itself</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ label, p }) => (
              <tr key={label}>
                <th scope="row">{label}</th>
                <td>{period(p.video)}</td>
                <td>{period(p.audio)}</td>
                <td>{period(p.transcript)}</td>
                <td>{period(p.meeting)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mx-muted">
        Set in the server&rsquo;s configuration, not here — a retention policy that could be
        changed from a web page is one that can be changed by whoever is having a bad week.
      </p>
    </section>
  );
}

function HoldsPanel({ isOp }: { isOp: boolean }) {
  const [holds, setHolds] = useState<Hold[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ reason: '', room: '', principal: '', from: '', to: '' });

  const load = useCallback(() => {
    fetchHolds()
      .then(setHolds)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);
  useEffect(load, [load]);

  const submit = () => {
    setError(null);
    placeHold({
      reason: form.reason,
      room: form.room || undefined,
      principal: form.principal || undefined,
      from: form.from || undefined,
      to: form.to || undefined,
    })
      .then(() => {
        setOpen(false);
        setForm({ reason: '', room: '', principal: '', from: '', to: '' });
        load();
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  };

  const lift = (id: string) => {
    setError(null);
    liftHold(id)
      .then(load)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  };

  const active = (holds ?? []).filter((h) => !h.lifted_at);
  const past = (holds ?? []).filter((h) => h.lifted_at);

  return (
    <section className="mx-dash-section">
      <div className="mx-room-head">
        <h2>Legal hold</h2>
        {isOp && !open && (
          <button className="mx-back" onClick={() => setOpen(true)}>
            Place a hold →
          </button>
        )}
      </div>
      <p className="mx-muted">
        Held material ignores the retention policy, and a deletion request against it is
        refused rather than quietly honoured — a legal obligation to retain outlives the right
        to erasure for as long as it lasts. Both placing and lifting are recorded.
      </p>

      {error && <p className="mx-error">{error}</p>}

      {open && (
        <div className="mx-erase-dialog">
          <h3>Place a legal hold</h3>
          <label>
            Why — this is the part that has to be defensible later
            <input
              autoFocus
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="Acme v. Contoso, matter 2026-114"
            />
          </label>
          <div className="mx-hold-scope">
            <label>
              Room (blank for every room)
              <input
                value={form.room}
                onChange={(e) => setForm({ ...form, room: e.target.value })}
                placeholder="board-prep"
              />
            </label>
            <label>
              Person, by principal (blank for everyone)
              <input
                value={form.principal}
                onChange={(e) => setForm({ ...form, principal: e.target.value })}
              />
            </label>
            <label>
              From
              <input
                type="date"
                value={form.from}
                onChange={(e) => setForm({ ...form, from: e.target.value })}
              />
            </label>
            <label>
              To
              <input
                type="date"
                value={form.to}
                onChange={(e) => setForm({ ...form, to: e.target.value })}
              />
            </label>
          </div>
          <p className="mx-muted">
            Leaving every field blank holds the entire archive. That is a real thing to want on
            the day a dispute starts, so it is allowed rather than treated as a mistake.
          </p>
          <div className="mx-erase-actions">
            <button className="mx-invite-btn" onClick={() => setOpen(false)}>
              Cancel
            </button>
            <button className="mx-button mx-button-inline" onClick={submit}>
              Place the hold
            </button>
          </div>
        </div>
      )}

      {!holds ? (
        <p className="mx-muted">Loading…</p>
      ) : holds.length === 0 ? (
        <p className="mx-muted">Nothing is under hold.</p>
      ) : (
        <>
          {active.map((h) => (
            <div className="mx-hold" key={h.id}>
              <span className="mx-hold-id">{h.id}</span>
              <span className="mx-hold-scope-line">{scopeOf(h)}</span>
              <span className="mx-hold-reason">{h.reason}</span>
              <span className="mx-muted">
                placed by {h.placed_by}, {when(h.placed_at)}
              </span>
              {isOp && (
                <button className="mx-invite-btn" onClick={() => lift(h.id)}>
                  Lift
                </button>
              )}
            </div>
          ))}
          {past.length > 0 && (
            <details className="mx-past-holds">
              <summary className="mx-disclose">
                {past.length} lifted hold{past.length === 1 ? '' : 's'}
              </summary>
              {past.map((h) => (
                <div className="mx-hold lifted" key={h.id}>
                  <span className="mx-hold-id">{h.id}</span>
                  <span className="mx-hold-scope-line">{scopeOf(h)}</span>
                  <span className="mx-hold-reason">{h.reason}</span>
                  <span className="mx-muted">
                    lifted by {h.lifted_by}, {when(h.lifted_at!)}
                  </span>
                </div>
              ))}
            </details>
          )}
        </>
      )}
    </section>
  );
}

function scopeOf(h: Hold): string {
  const parts: string[] = [];
  if (h.room) parts.push(`room ${h.room}`);
  if (h.principal) parts.push(`one person`);
  if (h.from) parts.push(`from ${new Date(h.from).toLocaleDateString()}`);
  if (h.to) parts.push(`to ${new Date(h.to).toLocaleDateString()}`);
  return parts.length > 0 ? parts.join(' · ') : 'the whole archive';
}

/** A one-line summary of an event, in words rather than in its field names. */
function describe(e: AuditEvent): string {
  const what: Record<string, string> = {
    'meeting.open': 'opened a meeting',
    'recording.play': 'played a track',
    'recording.download': 'downloaded a track',
    'meeting.download': 'downloaded a whole meeting',
    'transcript.export': 'exported a transcript',
    'transcript.follow': 'followed a live transcript',
    'call.say': 'posted into a call from the host',
    'call.tune': 'capped the video of someone in a call from the host',
    'call.mute': 'muted a track of someone in a call from the host',
    'call.unmute': 'unmuted a track of someone in a call from the host',
    'call.join': 'joined a comrade into a call from the host',
    'call.leave': 'took a comrade out of a call from the host',
    'call.grant': 'let a comrade control their own call client',
    'call.revoke': 'took back a comrade’s control of their call client',
    'call.control': 'controlled someone’s call client, as they allowed',
    'archive.search': 'searched the archive',
    'meeting.erase': 'erased somebody from a meeting',
    'retention.prune': 'applied the retention policy',
    'hold.place': 'placed a legal hold',
    'hold.lift': 'lifted a legal hold',
    'audit.read': 'read the audit log',
  };
  return what[e.event] ?? e.event;
}

function AuditPanel() {
  const [events, setEvents] = useState<AuditEvent[] | null>(null);
  const [configured, setConfigured] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [onlyDenied, setOnlyDenied] = useState(false);

  useEffect(() => {
    fetchAudit(500)
      .then((p) => {
        setConfigured(p.configured);
        setEvents(p.events);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  if (error) return <p className="mx-error">{error}</p>;

  const shown = (events ?? []).filter((e) => !onlyDenied || e.outcome === 'denied');

  return (
    <section className="mx-dash-section">
      <div className="mx-room-head">
        <h2>Access log</h2>
        {configured && (
          <a className="mx-back" href={auditExportUrl()} download>
            Export as JSONL →
          </a>
        )}
      </div>

      {!configured ? (
        <p className="mx-muted">
          No audit log is configured on this instance, so accesses are not being recorded. Set{' '}
          <code>services.meetnix.audit.file</code> to switch it on.
        </p>
      ) : (
        <>
          <p className="mx-muted">
            One line per access, including refusals. The export is the file itself — the same
            newline-delimited JSON a log shipper would read straight off disk, which is what
            &ldquo;in the format your SIEM already parses&rdquo; means here.
          </p>
          <div className="mx-chips">
            <button
              className={`mx-chip${onlyDenied ? ' on' : ''}`}
              onClick={() => setOnlyDenied((v) => !v)}
            >
              refusals only
            </button>
          </div>

          {!events ? (
            <p className="mx-muted">Loading…</p>
          ) : shown.length === 0 ? (
            <p className="mx-muted">{onlyDenied ? 'Nothing has been refused.' : 'Nothing yet.'}</p>
          ) : (
            <div className="mx-tablewrap">
              <table className="mx-table mx-audit">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Who</th>
                    <th>What</th>
                    <th>Where</th>
                    <th>From</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((e, i) => (
                    <tr key={i} className={e.outcome === 'denied' ? 'denied' : undefined}>
                      <td className="mx-audit-when">{when(e.ts)}</td>
                      <td>{e.actor.name || e.actor.principal || '—'}</td>
                      <td>
                        {e.outcome === 'denied' && <b>refused: </b>}
                        {describe(e)}
                        {e.reason && <span className="mx-muted"> — {e.reason}</span>}
                      </td>
                      <td className="mx-audit-where">
                        {e.room ? `${e.room}${e.session ? `/${e.session}` : ''}` : '—'}
                      </td>
                      <td className="mx-audit-where">{e.ip ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}

export function Admin({ isOp }: { isOp: boolean }) {
  // The frame supplies the bar, the nav and the account menu (AppShell); this
  // page is its own content only.
  return (
    <>
      <RetentionPanel />
      <HoldsPanel isOp={isOp} />
      {isOp ? (
        <AuditPanel />
      ) : (
        <section className="mx-dash-section">
          <div className="mx-room-head">
            <h2>Access log</h2>
          </div>
          <p className="mx-muted">
            Every access to a recording is recorded, refusals included. The log itself is
            limited to operators.
          </p>
        </section>
      )}
    </>
  );
}
